import { Injectable, NgZone, inject } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LoadingStateService {
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);

  private pendingRequests = 0;
  private isNavigating = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly excludedUrls: string[] = ['/health', '/ping', '/analytics'];

  init(): void {
    this.setupRouterListener();
    this.updateAttribute();
  }

  private setupRouterListener(): void {
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError
        )
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.isNavigating = true;
          this.updateAttribute();
        } else {
          this.isNavigating = false;
          this.updateAttribute();
        }
      });
  }

  requestStarted(url: string): void {
    if (this.shouldExclude(url)) return;
    this.pendingRequests++;
    this.updateAttribute();
  }

  requestCompleted(url: string): void {
    if (this.shouldExclude(url)) return;
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    this.updateAttribute();
  }

  private shouldExclude(url: string): boolean {
    return this.excludedUrls.some((excluded) => url.includes(excluded));
  }

  private updateAttribute(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    const isLoading = this.isNavigating || this.pendingRequests > 0;

    if (isLoading) {
      this.setAttribute('InProgress');
    } else {
      this.debounceTimer = setTimeout(() => {
        this.ngZone.runOutsideAngular(() => {
          requestAnimationFrame(() => {
            if (!this.isNavigating && this.pendingRequests === 0) {
              this.setAttribute('Done');
            }
          });
        });
      }, 50);
    }
  }

  private setAttribute(value: 'InProgress' | 'Done'): void {
    document.body.dataset['loading'] = value;
  }
}
